import { useCallback, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { BLUE, colorGenerator, GREEN } from "@/features/shared/widgets/graphs/helpers";
import getLineWidth from "@/lib/graph/get-line-width";
import getDashStyle from "@/lib/graph/get-dash-style";
import { withMarkerDensityCull } from "@/lib/graph/with-marker-density-cull-options";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { DimensionValue } from "@/lib/dimensions";
import { renderValue } from "@/features/dashboard/components/timeseries/render-value";
import { getYAxisPerUnit } from "@/components/graph/get-y-axis-for-unit";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import {
  chartSeriesIsDisabled,
  chartSeriesPresentationColor,
  formatChartSeriesName,
  getChartSeriesPresentationState,
  resolveSeriesDisplayLabel,
} from "@/components/graph/chart-series-presentation";
import { useTranslation } from "@/lib/i18n";

import useGraphOptions from "./use-graph-options";
import { resolveGraphChartHeight } from "./resolve-graph-chart-height";
import { useTimeSeriesNoData } from "./hooks/use-time-series-no-data";
import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";
import type { LineGraphTimeSeriesConfig } from "./graph.types";
import {
  buildThresholdPlotBands,
  buildThresholdPlotLines,
  buildThresholdZones,
} from "./thresholds";

interface LinegraphProps {
  config: LineGraphTimeSeriesConfig;
  data: ParameterTimeSeriesChartResult[];
  currentTime: DimensionValue;
  timeBounds?: ChartTimeBounds;
  height?: number;
}

const Linegraph = ({ config, data, currentTime, timeBounds, height }: LinegraphProps) => {
  const { t } = useTranslation();
  const timeRange = useSelectedTimeRange();

  const greenColorGenerator = colorGenerator(GREEN);
  const blueColorGenerator = colorGenerator(BLUE);

  const getColor = useCallback(
    (color: string) => {
      const fallback = "grey";
      if (color === "green") {
        return greenColorGenerator.next().value ?? fallback;
      }
      if (color === "blue") {
        return blueColorGenerator.next().value ?? fallback;
      }
      return fallback;
    },
    [greenColorGenerator, blueColorGenerator],
  );

  const thresholdZones = useMemo(
    () => buildThresholdZones(config.thresholds),
    [config.thresholds],
  );
  const thresholdPlotLines = useMemo(
    () => buildThresholdPlotLines(config.thresholds),
    [config.thresholds],
  );
  // Threshold bands must reflect only the series drawn in THIS chart. `data` is a
  // shared array that, on the dashboard, also carries the widget's other
  // configured parameters; evaluating those unrelated series against this
  // config's threshold produced bands spanning the entire chart.
  const seriesData = useMemo(() => {
    const ids = new Set(config.series.map((s) => s.parameter_id));
    return data.filter((result) => ids.has(result.parameter?.id));
  }, [config.series, data]);
  const thresholdPlotBands = useMemo(
    () => buildThresholdPlotBands(config.thresholds, seriesData),
    [config.thresholds, seriesData],
  );

  const seriesWithData = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    const seriesWithData = config.series
      .map((seriesConfig) => {
        const result = data.find(
          (timeSeriesResult) => timeSeriesResult.parameter.id === seriesConfig.parameter_id,
        );
        if (!result) return undefined;
        return {
          seriesConfig,
          result,
        };
      })
      .filter((it) => !!it);

    return seriesWithData.map(({ seriesConfig, result }) => {
      const presentationState = getChartSeriesPresentationState(result, timeRange);
      const displayLabel = resolveSeriesDisplayLabel(seriesConfig, result);
      const serieName = formatChartSeriesName(displayLabel, presentationState, t);
      const datapoints = result.data?.datapoints ?? [];
      const mappedDatapoints = datapoints.map((datapoint) => ({
        x: +new Date(datapoint.timestamp),
        y: datapoint.value,
      }));

      return {
        // base configuration
        type: "line",
        name: serieName,

        // line style configuration
        color: chartSeriesPresentationColor(
          presentationState,
          getColor(seriesConfig.color),
        ),
        lineWidth: getLineWidth(seriesConfig.line_width),
        dashStyle: getDashStyle(seriesConfig.line_style),
        marker: {
          symbol: seriesConfig.symbol,
          radius: 4,
          enabledThreshold: 0,
        },
        enableMouseTracking: !chartSeriesIsDisabled(presentationState),
        tooltip: {
          pointFormatter: function (this) {
            let symbol = "";
            switch (seriesConfig.symbol) {
              case "circle":
                symbol = "&#9679";
                break;
              case "triangle":
                symbol = "&#9650";
                break;
              case "triangle-down":
                symbol = "&#9660";
                break;
              case "diamond":
                symbol = "&#9670";
                break;
              case "square":
              default:
                symbol = "&#9632";
                break;
            }
            return `<span style="color: ${this.color}">${symbol}</span> ${
              this.series.name
            }: <strong>${renderValue(this.y, config.unit)}</strong>`;
          },
        },
        states: {
          inactive: {
            opacity: 1,
          },
        },
        zones: thresholdZones,

        // data configuration
        data: mappedDatapoints,
      };
    });
  }, [config, data, timeRange, getColor, t, thresholdZones]);

  const yAxisPerUnit = getYAxisPerUnit(config.unit);
  const chartHeight = resolveGraphChartHeight(height);
  const baseGraphOptions = useGraphOptions(
    {
      ...(chartHeight !== undefined ? { chart: { height: chartHeight } } : {}),
      title: {
        text: config.title,
      },
      yAxis: [
        { ...(yAxisPerUnit[0] ?? {}), plotLines: thresholdPlotLines },
        ...yAxisPerUnit.slice(1),
      ],
      xAxis: [
        {
          plotLines: [
            {
              id: "currentTime",
              color: "#01689B", // KNMI blue
              value: +new Date(currentTime.queryStringValue),
              // Sit above the threshold fill bands (zIndex 0) but below the data series (zIndex 3).
              zIndex: 2,
            },
          ],
          plotBands: thresholdPlotBands,
        },
      ],
      series: seriesWithData,
    },
    timeBounds,
  );

  const graphOptions = useMemo(
    () => withMarkerDensityCull(baseGraphOptions),
    [baseGraphOptions],
  );

  const noData = useTimeSeriesNoData(config, data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact highcharts={Highcharts} options={graphOptions} />
    </NoDataOverlay>
  );
};

export default Linegraph;
